import { View, ScrollView, RefreshControl, Vibration } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Alert } from '../../components/other/Alert';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import Toast from 'react-native-root-toast';
import * as Clipboard from 'expo-clipboard';

import { countDriverTickets, countDriverResolvedTickets } from '../../../common/utils/ticketList/countDriverTickets';
import { shouldMachineBeRestocked } from '../../../common/utils/serviceRuns/shouldMachineBeRestocked';
import { shouldMachineBeRepaired } from '../../../common/utils/serviceRuns/shouldMachineBeRepaired';
import { shouldConfirmStockLevels } from '../../../common/utils/serviceRuns/shouldConfirmStockLevels';
import getPOSModelName from '../../../common/utils/machine/getPOSModelName';
import { getFullMachineName } from '../../../common/utils/machine';
import { useUserContext } from '../../../common/utils/context';
import { getLocalTime } from '../../../common/utils/time/time';
import { formatDate } from '../../../common/utils/utils';
import { POS_TYPE, ROLE_PERMISSIONS } from '../../../common/constants';

import { Icon, Button, Separator, Heading, Dialog, WarningDialog, Text } from '../../components';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useRefetchOnFocus } from '../../hooks';
import { DetailContainer } from '../../containers';
import DocketCashBagDetail from './DocketCashBagDetail';
import DocketDetailItem from './DocketDetailItem';
import DocketTubDetail from './DocketTubDetail';
import {
	DocketCoinMechDetail,
	DocketLocationDetail,
	DocketPOSDetail,
	DocketServiceStatusDetail,
} from './docketDetails';
import ServiceRunVehicleDetail from '../servicerun/ServiceRunDetail/ServiceRunVehicleDetail';
import { ManualRestockDialog, useManualRestockFlow } from '../manualRestock';
import { useDidUpdateEffect, usePrevious } from '../../../common/utils/hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { useOfflineQueue } from '../../offline';
import { getMyDayTrainingRouteParams, isMyDayTraining } from '../myDay/myDayTraining';

const DocketDetail = ({
	refetch,
	id: docketId,
	data: docket,
	modifyItem: modifyServiceDocket,
	setRefetch,
	trainingContext,
}) => {
	const navigation = useNavigation();
	const {
		hasPermission,
		isSuperAdmin,
		tenant,
		tenantSettings,
		user,
	} = useUserContext();
	const { theme } = useTheme();
	const { isOnline, queue } = useOfflineQueue();
	const isTraining = isMyDayTraining(trainingContext);
	const scopedQueue = useMemo(() => (isTraining ? [] : queue), [isTraining, queue]);
	const trainingRouteParams = getMyDayTrainingRouteParams(trainingContext);
	const modifyServiceDocketForContext = useCallback((values) => {
		if (isTraining && !isOnline) {
			Toast.show('Reconnect to continue training');
			return undefined;
		}

		return modifyServiceDocket({
			...values,
			...(isTraining ? { adminTraining: true } : {}),
		});
	}, [isOnline, isTraining, modifyServiceDocket]);

	const { machine, isRestocked, startedProcessing, pickList, serviceRun, status, productExpiryCheckSchedules, hasSomeExpiryDatesToUpdate } = docket;
	const isAnyItemPicked = pickList?.isAnyItemPicked;
	const hasProductSoldSinceLastPickListRecalculation = pickList?.hasProductSoldSinceLastPickListRecalculation;
	const { planned } = serviceRun;
	useRefetchOnFocus(refetch);

	const isMarket = machine.type === POS_TYPE.MARKET;
	const isOffline = !machine.commDevices?.length;
	const pendingMachineInventoryOperation = useMemo(() => (scopedQueue || []).some(entry => (
		['manualRestock', 'confirmStockLevels', 'confirmMachineValues'].includes(entry.configKey) &&
		(entry.meta?.machineId === machine.id || entry.meta?.serviceDocketId === docketId)
	)), [docketId, machine.id, scopedQueue]);
	const isAssignedDriver = user?.id && serviceRun.driver?.id === user.id;
	const canManageServiceRuns = isSuperAdmin || hasPermission(ROLE_PERMISSIONS.ADD_EDIT_SERVICE_RUNS);
	const canCorrectMarketRestock = isOnline && isMarket && !isOffline &&
		isRestocked && docket.manualRestock && !docket.isIngredientRestock &&
		docket.marketRestockCorrectionAvailability?.allowed &&
		!pendingMachineInventoryOperation &&
		(isAssignedDriver || canManageServiceRuns);
	const requiresRestock = shouldMachineBeRestocked(docket.machineServiceType);
	const supportsStockConfirmation = shouldConfirmStockLevels({
		machineUsesIngredients: machine.usesIngredients,
		isOffline,
	});
	const plannedTime = getLocalTime(tenant.timezone, planned);
	const localTime = getLocalTime(tenant.timezone);

	// Auto-assigned vehicles must be actively confirmed by the driver on the day of the run
	const hasAssignedVehicle = Boolean(docket.serviceRun.vehicle?.id);
	const needsVehicleAssignmentOrConfirmation = !hasAssignedVehicle || !docket.serviceRun.vehicleConfirmed;

	const [isShowVehicleAssignmentDialogOpen, setIsShowVehicleAssignmentDialogOpen] = useState(plannedTime.isSame(localTime, 'day') && tenantSettings.showVehicleAssignmentDialog && needsVehicleAssignmentOrConfirmation && isRestocked);

	// Use hook to track previous isRestocked value
	const previousIsRestocked = usePrevious(isRestocked);

	const machineName = getFullMachineName(machine, machine.place, machine.location);

	const copyMachineName = useCallback(async () => {
		if (!machineName) {
			return;
		}
		// Mirror the web address copy interaction with feedback for drivers
		await Clipboard.setStringAsync(machineName);
		Toast.show(`${machineName} copied`);
	}, [machineName]);

	const [refreshPage, isRefreshing] = useRefreshPage(refetch);

	const [cashBagDialog, setCashBagDialog] = useState(false);
	const [tubDialog, setTubDialog] = useState(false);

	useEffect(() => {
		if (setRefetch && refetch) {
			setRefetch(() => refetch);
		}
	}, [refetch, setRefetch]);

	// Effect to handle vibration when isRestocked changes
	useDidUpdateEffect(() => {
		// Vibrate when isRestocked changes from false to true (machine got restocked)
		// This covers both automatic restocks (from DEX) and manual restocks
		if (isRestocked && !previousIsRestocked) {
			// Vibrate the phone to notify about restock
			Vibration.vibrate([0, 100]); // Pattern: wait 0ms, vibrate 100ms
		}
	}, [isRestocked, previousIsRestocked]);

	const requireCheckExpiryDatesWarningText = (tenantSettings?.useExpiryDates && (productExpiryCheckSchedules?.length || hasSomeExpiryDatesToUpdate)) ? 'Expiry dates require updates' : '';

	const driverTicketCount = useMemo(() => docket.ticketList && shouldMachineBeRepaired(docket.machineServiceType) && countDriverTickets(docket.ticketList.driverItems, tenantSettings), [docket.ticketList, docket.machineServiceType, tenantSettings]);
	const resolvedDriverTicketCount = useMemo(() => docket.ticketList && shouldMachineBeRepaired(docket.machineServiceType) && countDriverResolvedTickets(docket.ticketList.driverItems, tenantSettings), [docket.ticketList, docket.machineServiceType, tenantSettings]);

	const docketTicketIds = useMemo(() => {
		const ids = new Set();
		const driverItems = docket.ticketList?.driverItems || [];
		driverItems.forEach(driverItem => {
			if (driverItem.ticket?.id) {
				ids.add(driverItem.ticket.id);
			}
			else if (driverItem.id) {
				ids.add(driverItem.id);
			}
		});
		return ids;
	}, [docket.ticketList]);

	const pendingTicketCreations = useMemo(() => (scopedQueue || []).filter(entry => entry.configKey === 'createTicket' && entry.meta?.docketId === docketId).length, [scopedQueue, docketId]);
	const pendingTicketPhotos = useMemo(() => (scopedQueue || []).filter(entry => entry.configKey === 'updateTicketPhotos' && docketTicketIds.has(entry.meta?.ticketId)).length, [scopedQueue, docketTicketIds]);
	const pendingMediaUploads = useMemo(() => (scopedQueue || []).filter(entry => entry.configKey === 'createMedia' && (entry.meta?.serviceDocketId === docketId || entry.meta?.machineId === machine.id)).length, [scopedQueue, docketId, machine.id]);
	const pendingStockConfirmation = useMemo(() => (scopedQueue || []).some(entry => entry.configKey === 'confirmStockLevels' && entry.meta?.serviceDocketId === docketId), [scopedQueue, docketId]);
	const pendingValuesConfirmation = useMemo(() => (scopedQueue || []).some(entry => entry.configKey === 'confirmMachineValues' && entry.meta?.serviceDocketId === docketId), [scopedQueue, docketId]);
	const pendingManualRestock = useMemo(() => (scopedQueue || []).some(entry => entry.configKey === 'manualRestock' && entry.meta?.serviceDocketId === docketId), [scopedQueue, docketId]);

	const offlineBannerMessages = useMemo(() => {
		const messages = [];
		if (pendingTicketCreations) {
			messages.push(`${pendingTicketCreations} ticket${pendingTicketCreations > 1 ? 's' : ''}`);
		}
		if (pendingTicketPhotos) {
			messages.push(`${pendingTicketPhotos} photo${pendingTicketPhotos > 1 ? 's' : ''}`);
		}
		if (pendingManualRestock) {
			messages.push('restock');
		}
		if (pendingStockConfirmation) {
			messages.push('stock confirmation');
		}
		if (pendingValuesConfirmation) {
			messages.push('values confirmation');
		}
		if (pendingMediaUploads) {
			messages.push(`${pendingMediaUploads} photo${pendingMediaUploads > 1 ? 's' : ''}`);
		}
		return messages;
	}, [pendingTicketCreations, pendingTicketPhotos, pendingManualRestock, pendingStockConfirmation, pendingValuesConfirmation, pendingMediaUploads]);

	const hasOfflineBanner = offlineBannerMessages.length > 0;

	const posModelName = useMemo(() => getPOSModelName(machine), [machine]);

	const themedStyles = useThemedStyles(createThemedStyles);
	const vehicleAssignmentStyles = useThemedStyles(vehicleAssignmentDialogStyles);

	const {
		openManualRestockAlert,
		manualRestock,
		values,
		changeFormValue,
		isManualRestockWarningOpen,
		setIsManualRestockWarningOpen,
	} = useManualRestockFlow({
		serviceDocketId: docketId,
		isRestocked,
		startedProcessing,
		machine,
		planned,
		onManualRestockSuccess: () => {
			setIsShowVehicleAssignmentDialogOpen(tenantSettings.showVehicleAssignmentDialog && needsVehicleAssignmentOrConfirmation);
		},
		disableOffline: isTraining,
		adminTraining: isTraining,
	});

	const goToPicklistDetail = useCallback(() => navigation.push('PickListDetailView', {
		isAnyItemPicked,
		planned,
		status,
		hasProductSoldSinceLastPickListRecalculation,
		id: docket.pickList && docket.pickList.id,
		machineName,
		...trainingRouteParams,
	}), [navigation, isAnyItemPicked, planned, status, hasProductSoldSinceLastPickListRecalculation, docket.pickList, machineName, trainingRouteParams]);

	const goToMachinePhoto = useCallback(() => navigation.push('MediaListView', {
		docketId: docket.id,
		machineId: machine.id,
		machineName,
		isMarket,
		...trainingRouteParams,
	}), [navigation, docket.id, machine.id, machineName, isMarket, trainingRouteParams]);

	const goToTicketListDetail = useCallback(() => navigation.push('TicketListDetailView', {
		id: docket.ticketList.id,
		machineName,
		machineId: machine.id,
		docketId: docket.id,
		...trainingRouteParams,
	}), [navigation, docket.ticketList?.id, docket.id, machineName, machine.id, trainingRouteParams]);

	const goToConfirmStockLevels = useCallback(() => {

		if (requiresRestock && !isRestocked) {
			// Machine has not received a dex; you cannot yet confirm stock levels.
			Alert.alert(
				'Confirm stock levels',
				`You cannot confirm stock levels yet. Please wait for a restock communication to be received, or restock ${posModelName.toLowerCase()} manually if required.`,
				[
					{ text: 'Ok', onPress: () => console.log('OK Pressed') },
				],
				{ cancelable: false },
			);
		}
		else {
			navigation.push('ConfirmStockLevelsView', { docketId, ...trainingRouteParams });
		}
	}, [requiresRestock, isRestocked, posModelName, navigation, docketId, trainingRouteParams]);

	const goToConfirmMachineValues = useCallback(() => {
		// added this check as we also show this view for online coffee machines
		if (isRestocked || isOffline) {
			navigation.push('ConfirmMachineValuesView', {
				docketId,
				isCoffee: machine.usesIngredients,
				...trainingRouteParams,
			});
		}
		else {
			Alert.alert(
				'Confirm restock values',
				`You cannot confirm restock values yet. Please wait for a restock communication to be received, or restock the ${posModelName.toLowerCase()} manually if required.`,
				[
					{ text: 'Ok', onPress: () => console.log('OK Pressed') },
				],
				{ cancelable: false },
			);
		}
	}, [isRestocked, isOffline, posModelName, navigation, docketId, machine.usesIngredients, trainingRouteParams]);

	const goToCorrectMarketRestock = useCallback(() => navigation.push('CorrectMarketRestockView', {
		docketId,
		machineName,
		...trainingRouteParams,
	}), [docketId, machineName, navigation, trainingRouteParams]);

	return (
		<View style={themedStyles.container}>
			<ScrollView
				style={themedStyles.scrollView}
				scrollIndicatorInsets={{ right: 1 }}
				refreshControl={
					(
						<RefreshControl
							refreshing={isRefreshing}
							onRefresh={refreshPage}
						/>
					)
				}
			>
				<View style={{ flexDirection: 'column', marginLeft: 8, marginRight: 8 }}>
					<View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
						<View style={{ flex: 1 }}>
							<Heading
								heading={machineName}
								onHeadingPress={copyMachineName}
								subHeadingIcon={(
									<Icon
										name="calendar"
										size={18}
										style={{ marginRight: 4, width: 16 }}
									/>
								)}
								subHeading={docket.serviceRun.planned
									? `${formatDate(docket.serviceRun.planned, 'DAY_MONTHWORD_YEAR')}`
									: 'No date'}
							/>
						</View>
					</View>
					{hasOfflineBanner && (
						<View style={themedStyles.offlineBanner}>
							<Text style={themedStyles.offlineBannerText}>
								{`Pending sync: ${offlineBannerMessages.join(', ')}`}
							</Text>
						</View>
					)}
					<DocketLocationDetail docket={docket} styles={themedStyles} />
					<DocketPOSDetail docket={docket} styles={themedStyles} />
					<DocketServiceStatusDetail docket={docket} styles={themedStyles} />

					<Separator style={{ marginTop: 12, marginLeft: -8, marginRight: -8 }} />
				</View>
				<View>
					{shouldMachineBeRestocked(docket.machineServiceType) && !isOffline && (
						<DocketDetailItem
							label="Pick list"
							subLabel={`${posModelName} pick list items`}
							onPress={goToPicklistDetail}
						/>
					)}
					{
						docket.ticketList && shouldMachineBeRepaired(docket.machineServiceType) && driverTicketCount > 0 && (
							<DocketDetailItem
								label={`Tickets (${resolvedDriverTicketCount}/${driverTicketCount})`}
								subLabel="Resolve machine tickets"
								onPress={goToTicketListDetail}
							/>
						)
					}
					<DocketCoinMechDetail docket={docket} />
					{
						docket.collectCash && !docket.serviceRun.isOneCashBag && (
							<DocketDetailItem
								label="Cash bag"
								subLabel={((docket.cashBag !== undefined) && (docket.cashBag !== null)) ? `Cash bag: ${docket.cashBag}` : 'No cash bag set'}
								onPress={() => setCashBagDialog(true)}
							/>
						)
					}
					{
						tenantSettings.enableTubsFieldOnDockets && (
							<DocketDetailItem
								label="Tub"
								subLabel={((docket.tub !== undefined) && (docket.tub !== null)) ? `Tub: ${docket.tub}` : 'No tub'}
								onPress={() => setTubDialog(true)}
							/>
						)
					}
					{
						docket.requirePhoto && (
							<DocketDetailItem
								label={`${getPOSModelName(machine)} photos`}
								subLabel={docket.photoTaken ? 'Photo has been taken' : 'At least 1 photo is required'}
								warning={!docket.photoTaken}
								onPress={goToMachinePhoto}
								rightLabel={
									<Icon name="alert" size={24} color={theme.text1} />
								}
							/>
						)
					}
					{
						supportsStockConfirmation && (
							<DocketDetailItem
								label="Confirm stock levels"
								subLabel={`Confirm ${posModelName.toLowerCase()} stock levels`}
								onPress={goToConfirmStockLevels}
								subText={requireCheckExpiryDatesWarningText || undefined}
								subTextAsWarning={!docket.checkedProductExpirySchedules?.length}
							/>
						)
					}
					{
						// should be restocked && use ingredients
						shouldMachineBeRestocked(docket.machineServiceType) && (machine.usesIngredients || isOffline) && (
							<DocketDetailItem
								label="Confirm values"
								subLabel="Confirm restock values"
								onPress={goToConfirmMachineValues}
							/>
						)
					}
					{
						canCorrectMarketRestock && (
							<Button
								label="Correct delivered quantities"
								secondary={true}
								onPress={goToCorrectMarketRestock}
								style={{
									margin: 10,
									marginTop: 10,
									marginBottom: 32,
								}}
							/>
						)
					}
					{
						shouldMachineBeRestocked(docket.machineServiceType) && !isOffline && !isRestocked && (
							<Button
								label={isMarket ? 'Restock' : 'Manual restock'}
								secondary={true}
								onPress={openManualRestockAlert}
								disabled={isTraining && !isOnline}
								icon={<Icon size={12} name="signal" />}
								style={{
									margin: 10,
									marginTop: 10,
									marginBottom: 32,
								}}
							/>
						)
					}
				</View>

			</ScrollView>
			<WarningDialog
				title="Vehicle assignment"
				texts={[hasAssignedVehicle
					? 'Confirm the vehicle for the current service run to continue'
					: 'Assign a vehicle to the current service run to continue']}
				subtext={hasAssignedVehicle
					? 'Please confirm the assigned vehicle is the one you are driving today, or select a different vehicle.'
					: 'Please assign a vehicle using the button below to be able to close this dialog and continue.'}
				setIsOpen={setIsShowVehicleAssignmentDialogOpen}
				isOpen={isShowVehicleAssignmentDialogOpen}
				hideButtons={true}
				enableCloseDialog={isSuperAdmin || (hasAssignedVehicle && Boolean(serviceRun.vehicleConfirmed))}
			>
				<ServiceRunVehicleDetail
					servicerun={serviceRun}
					styles={vehicleAssignmentStyles}
					setShowDialogOpen={setIsShowVehicleAssignmentDialogOpen}
					showConfirmButton={true}
					adminTraining={isTraining}
					disableActions={isTraining && !isOnline}
				/>
			</WarningDialog>

			<Dialog
				isOpen={cashBagDialog}
				onClose={() => setCashBagDialog(false)}
			>
				<DocketCashBagDetail
					cashBag={docket.cashBag}
					modifyServiceDocket={modifyServiceDocketForContext}
					disabled={isTraining && !isOnline}
				/>
			</Dialog>

			<Dialog
				isOpen={tubDialog}
				onClose={() => setTubDialog(false)}
			>
				<DocketTubDetail
					tub={docket.tub}
					modifyServiceDocket={modifyServiceDocketForContext}
					disabled={isTraining && !isOnline}
				/>
			</Dialog>
			<ManualRestockDialog
				isOpen={isManualRestockWarningOpen}
				setIsOpen={setIsManualRestockWarningOpen}
				onClick={manualRestock}
				values={values}
				changeFormValue={changeFormValue}
			/>

		</View>
	);
};


const createThemedStyles = (theme) => ({
	container: {
		backgroundColor: theme.mainBg1,
		flex: 1,
	},
	scrollView: {
		backgroundColor: theme.mainBg1,
	},
	headerText: {
		fontSize: 20,
		marginTop: 2,
		color: theme.text1,
	},
	detailView: {
		flexDirection: 'column',
		marginBottom: 4,
	},
	separator: {
		marginLeft: -8,
		marginRight: -8,
		marginBottom: 8,
		marginTop: 8,
	},
	icon: {
		width: 26,
		alignItems: 'center',
	},
	typographyParagraph: {
		fontSize: 16,
		color: theme.text1,
	},
	offlineBanner: {
		backgroundColor: theme.mainBg3,
		borderRadius: 6,
		paddingVertical: 6,
		paddingHorizontal: 10,
		marginTop: 8,
		marginBottom: 8,
	},
	offlineBannerText: {
		color: theme.text2,
		fontSize: 14,
	},
});

const vehicleAssignmentDialogStyles = (theme) => ({
	firstButton: {
		flex: 1,
	},
	secondButton: {
		marginLeft: 10,
		flex: 1,
	},
	headerText: {
		fontSize: 20,
		marginTop: 2,
		color: theme.text1,
	},
});

export default compose(DetailContainer)(DocketDetail);
